import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { formatCardDate, labelBase, borderBase, inputBase } from '../components/shared/helpers';
import { useBoardingPoint } from '../components/shared/useBoardingPoint';
import { tncClauses } from './TermsAndConditions';
import { downloadTicket } from '../utils/ticketPDF';
import { sendTicketWhatsApp, generateAndSendInvoice, toDdMmYyyy } from '../lib/postBooking';
import { CONTACT_NUMBERS, telHref } from '../lib/contact';

// /t/:token — a staff-issued table booking link (Stage 3).
//
// A member of staff picked these exact tables on the seating sheet and sent
// this guest a private, time-limited link on WhatsApp. There is no OTP:
// receiving the link on that phone IS the verification, and a timer is
// running. The page never reads table_links itself (anon has no access to it);
// everything comes through the table-link edge function, which answers with
// only what this page shows.
//
// Money: the amount shown is the amount the LINK carries, and the Razorpay
// order is created server-side from that same row. Nothing typed here
// (the name) or shown here is ever sent as a price.
//
// One live order per link (GAPS #18): create-order hands back the link's
// existing unpaid order when there is one, so a second tab (or a retry after
// a declined card) opens checkout on the SAME order and Razorpay takes at most
// one payment for it. If the link has already been paid, create-order answers
// payment_received and the page says so instead of opening checkout.
//
// Verify + book reuse the `payment` function exactly like the ordinary
// checkout; the book action routes a link payment to its own RPC by the
// payment row's table_link_id, with the token as this page's identity.

const RZP_SCRIPT = 'https://checkout.razorpay.com/v1/checkout.js';
const TOKEN_RE = /^[A-Za-z0-9_-]{32}$/;

const GENERIC_ERROR = 'Something went wrong starting your payment. Please try again.';
const VERIFY_FAIL_MSG = "We couldn't verify your payment. If any amount was deducted, please contact us with your details and we'll sort it out — do not pay again.";
const BOOK_FAIL_MSG = 'Your payment was received, but we could not finalise your booking automatically. Our team has your payment on record and will confirm your booking or arrange a refund shortly. Please do not pay again.';

// Each dead state is a different sentence to the person reading it. Where
// the person is stuck and needs a human (expired, cancelled, unknown), `call`
// renders a prompt to phone us with both numbers as tel: links: `lead` goes
// before the numbers, `tail` after. "used" needs nothing from them, so it
// has no call prompt.
const DEAD_STATES = {
  expired: {
    title: 'This link has expired',
    body: 'The tables we were holding for you have been released.',
    call: { lead: 'Call us on ', tail: ' and we will send a fresh link if the tables are still free.' },
  },
  used: {
    title: 'This booking is already paid for',
    body: 'Your tables are confirmed and your ticket has been sent to your WhatsApp. If you need it again, reply to the message that sent you this link.',
  },
  cancelled: {
    title: 'This link has been cancelled',
    body: 'The tables for this link are no longer being held.',
    call: { lead: 'Call us on ', tail: ' if you still want to book.' },
  },
  unknown: {
    title: "We can't find this link",
    body: 'Please open the full link from your WhatsApp message.',
    call: { lead: 'If it still does not work, call us on ', tail: ' and we will help.' },
  },
  error: {
    title: 'Something went wrong',
    body: 'We could not load your link just now. Please try again in a moment.',
  },
};

function guestsLabel(adults, children, infants) {
  return [
    `${adults} ${adults === 1 ? 'Adult' : 'Adults'}`,
    children > 0 ? `${children} ${children === 1 ? 'Child' : 'Children'}` : null,
    infants > 0 ? `${infants} ${infants === 1 ? 'Infant' : 'Infants'}` : null,
  ].filter(Boolean).join(' · ');
}

// Countdown text: mm:ss under an hour, "1h 05m" above it.
function countdownLabel(seconds) {
  if (seconds >= 3600) {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    return `${h}h ${String(m).padStart(2, '0')}m`;
  }
  const mm = String(Math.floor(seconds / 60)).padStart(2, '0');
  const ss = String(seconds % 60).padStart(2, '0');
  return `${mm}:${ss}`;
}

const btnPrimary = 'w-full py-[15px] bg-[#C9A84C] text-[#0A1628] font-dm-sans font-semibold text-[12.5px] tracking-[0.15em] uppercase rounded-full transition-[transform,opacity,background-color] duration-[200ms] ease-[cubic-bezier(0.23,1,0.32,1)] focus-visible:outline-none';
const btnEnabled = 'opacity-100 active:scale-[0.98] hover:bg-[#B8973E] focus-visible:ring-2 focus-visible:ring-[rgba(201,168,76,0.5)] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0A1628] cursor-pointer';
const btnDisabled = 'opacity-60 cursor-not-allowed';

export default function TableLink() {
  const { token } = useParams();

  // state: 'loading' | 'active' | 'expired' | 'used' | 'cancelled' | 'unknown' | 'error'
  const [state, setState] = useState('loading');
  const [link, setLink] = useState(null);
  const [usedReference, setUsedReference] = useState('');

  const [name, setName] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);

  // phase: 'ready' | 'working' | 'verifying' | 'booking' | 'confirmed' | 'seating_failed' | 'payment_received' | 'failed'
  const [phase, setPhase] = useState('ready');
  const [errorMsg, setErrorMsg] = useState('');
  const [confirmed, setConfirmed] = useState(null); // { reference, bookingId, guestPhone }
  const [scriptReady, setScriptReady] = useState(false);

  // Countdown, driven from the server's clock: the function returns its own
  // "now" with the expiry, so a wrong device clock cannot show the wrong time.
  const [secondsLeft, setSecondsLeft] = useState(0);
  const clockOffsetRef = useRef(0); // serverNow - deviceNow, in ms
  const phaseRef = useRef(phase);
  useEffect(() => { phaseRef.current = phase; }, [phase]);

  const boardingPoint = useBoardingPoint(link?.date ?? null);
  const clauses = tncClauses(boardingPoint);

  // Load the link.
  useEffect(() => {
    let cancelled = false;
    if (!TOKEN_RE.test(String(token || ''))) {
      setState('unknown');
      return undefined;
    }
    (async () => {
      try {
        const { data, error } = await supabase.functions.invoke('table-link', {
          body: { action: 'get', token },
        });
        if (cancelled) return;
        if (error || !data || !data.state) {
          setState('error');
          return;
        }
        if (data.state !== 'active') {
          setUsedReference(data.reference || '');
          setState(data.state in DEAD_STATES ? data.state : 'unknown');
          return;
        }
        clockOffsetRef.current = new Date(data.serverNow).getTime() - Date.now();
        setLink(data);
        setName(data.customerName || '');
        setState('active');
      } catch (err) {
        console.error('table-link get failed:', err);
        if (!cancelled) setState('error');
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  // Tick the countdown. When it reaches zero while the guest is still
  // deciding, the link is dead. A payment already in flight is NOT
  // interrupted: once the order exists the server has extended the hold by
  // the grace window, and the booking outcome arrives on its own.
  useEffect(() => {
    if (state !== 'active' || !link?.expiresAt) return undefined;
    const expMs = new Date(link.expiresAt).getTime();
    const tick = () => {
      const now = Date.now() + clockOffsetRef.current;
      const remaining = Math.max(0, Math.ceil((expMs - now) / 1000));
      setSecondsLeft(remaining);
      return remaining;
    };
    if (tick() <= 0) {
      if (phaseRef.current === 'ready') setState('expired');
      return undefined;
    }
    const timer = setInterval(() => {
      if (tick() <= 0 && phaseRef.current === 'ready') {
        clearInterval(timer);
        setState('expired');
      }
    }, 1000);
    return () => clearInterval(timer);
  }, [state, link, phase]);

  // Load the Razorpay Checkout script once (same approach as Payment.js).
  useEffect(() => {
    if (state !== 'active') return;
    if (window.Razorpay) { setScriptReady(true); return; }
    const existing = document.querySelector(`script[src="${RZP_SCRIPT}"]`);
    if (existing) {
      if (existing.getAttribute('data-loaded')) setScriptReady(true);
      else existing.addEventListener('load', () => setScriptReady(true));
      return;
    }
    const s = document.createElement('script');
    s.src = RZP_SCRIPT;
    s.async = true;
    s.onload = () => { s.setAttribute('data-loaded', 'true'); setScriptReady(true); };
    s.onerror = () => setScriptReady(false);
    document.body.appendChild(s);
  }, [state]);

  // Verify the signature, then book — the same two `payment` calls the
  // ordinary checkout makes, with the token as this page's identity.
  const finalize = useCallback(async (resp) => {
    setPhase('verifying');
    try {
      const { data: vData, error: vErr } = await supabase.functions.invoke('payment', {
        body: {
          action: 'verify',
          razorpay_order_id: resp.razorpay_order_id,
          razorpay_payment_id: resp.razorpay_payment_id,
          razorpay_signature: resp.razorpay_signature,
        },
      });
      if (vErr || !vData || !vData.verified) {
        setErrorMsg(VERIFY_FAIL_MSG);
        setPhase('failed');
        return;
      }

      setPhase('booking');
      const bookingName = name.trim() || link.customerName || 'Guest';
      const { data: bData, error: bErr } = await supabase.functions.invoke('payment', {
        body: {
          action: 'book',
          razorpay_order_id: resp.razorpay_order_id,
          token,
          bookingName,
        },
      });

      if (!bErr && bData && bData.booked) {
        const reference = bData.reference || '';
        const guestPhone = bData.guestPhone || '';
        setConfirmed({ reference, bookingId: bData.bookingId, guestPhone });
        setPhase('confirmed');

        // Ticket + invoice to the guest's WhatsApp, exactly as the ordinary
        // checkout does — fire-and-forget, never awaited, never fatal.
        if (guestPhone) {
          const dateDdMmYyyy = toDdMmYyyy(link.date);
          sendTicketWhatsApp(
            {
              id: bData.bookingId,
              booking_name: bookingName,
              cruise_type: link.cruiseName,
              deck: link.deck,
              date: dateDdMmYyyy,
              adults: link.adults,
              children: link.children,
              infants: link.infants,
              tour_leaders: 0,
              guest_phone: guestPhone,
              partner_phone: null,
            },
            reference,
          );
          generateAndSendInvoice(bData.bookingId, guestPhone, dateDdMmYyyy)
            .catch((err) => console.error('Invoice generate/send failed (booking confirmed):', err));
        }
        return;
      }

      if (bData && bData.seating_failed) {
        setPhase('seating_failed');
        return;
      }

      setErrorMsg(BOOK_FAIL_MSG);
      setPhase('failed');
    } catch (e) {
      setErrorMsg(BOOK_FAIL_MSG);
      setPhase('failed');
    }
  }, [token, name, link]);

  // Start payment: the server creates the order from the LINK's amount,
  // records terms acceptance and extends the hold; then Razorpay opens.
  const handlePay = async () => {
    if (phase !== 'ready') return;
    if (!agreed || !name.trim()) return;
    if (!scriptReady || !window.Razorpay) {
      setErrorMsg('The payment window is still loading. Please try again in a moment.');
      return;
    }

    setErrorMsg('');
    setPhase('working');
    try {
      const { data, error } = await supabase.functions.invoke('table-link', {
        body: { action: 'create-order', token, name: name.trim(), termsAccepted: true },
      });

      if (data && typeof data.error === 'string' && data.error.startsWith('link_')) {
        // The link died between loading and paying — show the right dead state.
        const dead = data.error.slice('link_'.length);
        setPhase('ready');
        setState(dead in DEAD_STATES ? dead : 'unknown');
        return;
      }
      if (data && data.error === 'payment_received') {
        // Already paid — in another tab, or here a moment ago. No checkout.
        setPhase('payment_received');
        return;
      }
      if (error || !data || data.error || !data.orderId) {
        setPhase('ready');
        setErrorMsg(GENERIC_ERROR);
        return;
      }

      const rzp = new window.Razorpay({
        key: data.keyId,            // public checkout key, from the server
        amount: data.amount,        // the link's amount in paise, from the server
        currency: data.currency,
        order_id: data.orderId,
        name: 'Alfresco Grand',
        description: `${link.cruiseLabelShort} · ${link.deckLabel}`,
        prefill: { name: data.name || name.trim() },
        theme: { color: '#C9A84C' },
        handler: (resp) => { finalize(resp); },
        modal: {
          // Dismissed without paying — back to the page; the countdown decides
          // whether the link is still live.
          ondismiss: () => { setPhase((p) => (p === 'working' ? 'ready' : p)); },
        },
      });
      rzp.on('payment.failed', () => {
        setPhase('ready');
        setErrorMsg('Your payment could not be completed. Please try again.');
      });
      rzp.open();
    } catch (e) {
      setPhase('ready');
      setErrorMsg(GENERIC_ERROR);
    }
  };

  const handleDownload = async () => {
    if (!confirmed || !link) return;
    try {
      await downloadTicket(
        {
          id: confirmed.bookingId || null,
          booking_name: name.trim() || link.customerName || 'Guest',
          cruise_type: link.cruiseName,
          deck: link.deck,
          date: toDdMmYyyy(link.date),
          adults: link.adults,
          children: link.children,
          infants: link.infants,
          tour_leaders: 0,
        },
        confirmed.reference,
      );
    } catch (err) {
      console.error('Ticket download failed:', err);
    }
  };

  const wrap = 'w-full max-w-[600px] mx-auto px-0 md:px-2 card-enter';
  const h1 = 'font-cormorant font-normal text-[2rem] md:text-[2.4rem] leading-[1.1] tracking-[-0.01em] text-white';
  const notice = 'rounded-xl border border-[rgba(212,168,67,0.4)] bg-[rgba(201,168,76,0.08)] px-5 py-5';
  const bodyText = 'font-dm-sans text-sm font-light text-white/75 leading-relaxed';

  // ── Loading ────────────────────────────────────────────────────────────────
  if (state === 'loading') {
    return (
      <div className={wrap}>
        <p className="font-dm-sans text-sm font-light text-white/55 text-center py-10">Checking your link…</p>
      </div>
    );
  }

  // ── Dead states ────────────────────────────────────────────────────────────
  if (state !== 'active') {
    const copy = DEAD_STATES[state] || DEAD_STATES.unknown;
    return (
      <div className={wrap}>
        <h1 className={`${h1} mb-4`}>{copy.title}</h1>
        <div className={notice}>
          <p className={bodyText}>{copy.body}</p>
          {copy.call && (
            <p className={`${bodyText} mt-3`}>
              {copy.call.lead}
              {CONTACT_NUMBERS.map((n, i) => (
                <span key={n}>
                  {i > 0 && ' or '}
                  <a href={telHref(n)} className="text-[#C9A84C] hover:underline underline-offset-2 transition-colors duration-200">{n}</a>
                </span>
              ))}
              {copy.call.tail}
            </p>
          )}
          {state === 'used' && usedReference && (
            <p className="font-dm-sans text-sm text-white mt-3">
              Booking reference <span className="font-cormorant text-[1.35rem] text-[#C9A84C] tracking-[0.02em] ml-1">{usedReference}</span>
            </p>
          )}
        </div>
      </div>
    );
  }

  // ── Confirmed ──────────────────────────────────────────────────────────────
  if (phase === 'confirmed' && confirmed) {
    return (
      <div className={wrap}>
        <div className="flex flex-col items-center text-center">
          <div className="check-pop mb-3">
            <svg viewBox="0 0 52 52" className="w-[72px] h-[72px] md:w-16 md:h-16" aria-hidden="true">
              <circle cx="26" cy="26" r="24" fill="rgba(201,168,76,0.08)" stroke="#C9A84C" strokeWidth="2" />
              <path className="check-draw" d="M16 27 L23 34 L37 19" fill="none" stroke="#FFFFFF" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <h1 className="font-cormorant font-normal text-[2.4rem] md:text-[2.2rem] leading-[1.05] tracking-[-0.01em] text-white mb-1">
            Booking Confirmed
          </h1>
          <p className="font-dm-sans text-sm md:text-[15px] font-medium text-[rgba(212,168,67,0.85)] mb-5">
            See you on the Brahmaputra
          </p>
        </div>

        <div className="rounded-xl border border-[#C9A84C] bg-[#162840] px-5 py-5 mb-4 text-center">
          <p className="font-dm-sans text-[13px] uppercase tracking-[0.22em] font-medium text-white/60 mb-2">Booking Reference</p>
          <p className="font-cormorant text-[2rem] leading-none text-[#C9A84C] tracking-[0.02em] mb-2.5">{confirmed.reference}</p>
          <p className="font-dm-sans text-sm font-light text-white/55">Please keep this reference handy at boarding</p>
        </div>

        <div className="rounded-xl border border-white/[0.09] bg-[#162840] p-4 mb-4">
          {[
            ['Date', formatCardDate(link.date)],
            ['Cruise', link.cruiseLabel],
            ['Deck', link.deckLabel],
            ['Time', link.departureTime],
            ['Reporting Time', link.reportingTime],
            ['Boarding', boardingPoint],
            ['Guests', guestsLabel(link.adults, link.children, link.infants)],
          ].map(([label, value], i) => (
            <div key={label} className={`flex items-start justify-between gap-6 py-2.5 ${i > 0 ? 'border-t border-white/[0.06]' : ''}`}>
              <span className="font-dm-sans text-[13px] uppercase tracking-[0.1em] font-medium text-[rgba(212,168,67,0.85)] shrink-0">{label}</span>
              <span className={`font-dm-sans text-sm font-light text-white text-right leading-relaxed min-w-0 break-words ${label === 'Boarding' ? 'max-w-[62%]' : ''}`}>{value}</span>
            </div>
          ))}
          <div className="flex items-center justify-between gap-4 pt-3 mt-1 border-t border-[rgba(212,168,67,0.25)]">
            <span className="font-dm-sans text-sm font-medium text-white">Total Paid</span>
            <span className="font-dm-sans text-lg font-bold text-[#C9A84C] tabular-nums">&#8377;{Number(link.amount).toLocaleString('en-IN')}</span>
          </div>
        </div>

        <button type="button" onClick={handleDownload} className={`${btnPrimary} ${btnEnabled}`}>
          Download Ticket
        </button>
        <p className="font-dm-sans text-center text-sm font-medium text-white/70 leading-relaxed mt-3">
          Your ticket and invoice have been sent to your WhatsApp
        </p>
        <p className="font-dm-sans text-center text-sm font-light text-white/55 leading-relaxed mt-0.5 mb-2">
          They may take a few minutes to arrive.
        </p>
      </div>
    );
  }

  // ── Already paid (other tab / earlier attempt); booking follows from it ────
  if (phase === 'payment_received') {
    return (
      <div className={wrap}>
        <h1 className={`${h1} mb-4`}>Payment already received</h1>
        <div className={notice}>
          <p className={`${bodyText} mb-3`}>
            A payment for this link has already gone through, so there is nothing more to pay. Your booking is being confirmed from that payment.
          </p>
          <p className={bodyText}>
            Your ticket and invoice will arrive on WhatsApp shortly. If they have not arrived in a few minutes, reply to the message that sent you this link.
          </p>
        </div>
      </div>
    );
  }

  // ── Paid, but no seat could be secured ─────────────────────────────────────
  if (phase === 'seating_failed') {
    return (
      <div className={wrap}>
        <h1 className={`${h1} mb-4`}>Payment received</h1>
        <div className={notice}>
          <p className={`${bodyText} mb-3`}>
            Your payment was successful, but we couldn't secure your tables. No booking has been made and you will not be charged again.
          </p>
          <p className={bodyText}>
            Our team has been alerted and will contact you shortly to arrange a full refund or seat you another way. We're sorry for the inconvenience.
          </p>
        </div>
      </div>
    );
  }

  // ── Verify / book failed ───────────────────────────────────────────────────
  if (phase === 'failed') {
    return (
      <div className={wrap}>
        <h1 className={`${h1} mb-4`}>We hit a snag</h1>
        <div className={notice}>
          <p className={bodyText}>{errorMsg}</p>
        </div>
      </div>
    );
  }

  // ── Active link ────────────────────────────────────────────────────────────
  const busy = phase !== 'ready';
  const canPay = agreed && name.trim().length > 0 && !busy;
  const urgent = secondsLeft > 0 && secondsLeft < 300;

  return (
    <div className={wrap}>
      {/* Countdown */}
      <div className="flex items-center justify-between gap-4 rounded-lg border border-[#D4A843] bg-[rgba(201,168,76,0.1)] px-5 py-4 mb-4 shrink-0">
        <div className="flex flex-col">
          <span className="font-dm-sans text-[13px] text-white/60">Tables held for you for</span>
          <span className={`font-cormorant text-3xl leading-none mt-1 tabular-nums ${urgent ? 'text-[#E5484D]' : 'text-[#C9A84C]'}`}>
            {countdownLabel(secondsLeft)}
          </span>
        </div>
        <span className="font-dm-sans text-sm text-white font-medium text-right max-w-[160px] leading-relaxed">
          {busy ? 'Your payment is in progress' : 'Complete your booking before time runs out'}
        </span>
      </div>

      <h1 className={`${h1} mb-1.5`}>Your tables are waiting</h1>
      <p className="font-dm-sans text-sm font-light text-white/60 leading-relaxed mb-5">
        We have set aside these tables for you. Confirm your name, accept the terms and pay to make the booking yours.
      </p>

      {/* Summary */}
      <div className="rounded-xl border border-white/[0.09] bg-[#162840] p-4 mb-5">
        {[
          ['Date', formatCardDate(link.date)],
          ['Cruise', link.cruiseLabel],
          ['Deck', link.deckLabel],
          ['Departure', link.departureTime],
          ['Guests', guestsLabel(link.adults, link.children, link.infants)],
        ].map(([label, value], i) => (
          <div key={label} className={`flex items-start justify-between gap-6 py-2.5 ${i > 0 ? 'border-t border-white/[0.06]' : ''}`}>
            <span className="font-dm-sans text-[13px] uppercase tracking-[0.1em] font-medium text-[rgba(212,168,67,0.85)] shrink-0">{label}</span>
            <span className="font-dm-sans text-sm font-light text-white text-right leading-relaxed min-w-0 break-words">{value}</span>
          </div>
        ))}
        <div className="flex items-center justify-between gap-4 pt-3 mt-1 border-t border-[rgba(212,168,67,0.25)]">
          <span className="font-dm-sans text-sm font-medium text-white">Amount to pay <span className="text-white/55 font-light">(GST inclusive)</span></span>
          <span className="font-dm-sans text-lg font-bold text-[#C9A84C] tabular-nums">&#8377;{Number(link.amount).toLocaleString('en-IN')}</span>
        </div>
      </div>

      {/* Name */}
      <div className="group mb-5">
        <label htmlFor="tl-name" className={`${labelBase} block mb-2`}>Booking name</label>
        <div className={borderBase}>
          <input
            id="tl-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={busy}
            autoComplete="name"
            maxLength={120}
            placeholder="Name for the booking"
            className={`${inputBase} placeholder:text-white/30`}
          />
        </div>
        {link.phoneMasked && (
          <p className="font-dm-sans text-[13px] font-light text-white/45 mt-2">
            Ticket and invoice will be sent to WhatsApp {link.phoneMasked}
          </p>
        )}
      </div>

      {/* Terms */}
      <div className="rounded-xl border border-white/[0.09] bg-[#162840] p-4 mb-5">
        <label className="flex items-start gap-3 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
            disabled={busy}
            className="mt-[3px] h-4 w-4 shrink-0 accent-[#C9A84C] cursor-pointer"
          />
          <span className="font-dm-sans text-sm font-light text-white/80 leading-relaxed">
            I have read and agree to the terms and conditions, including the cancellation policy.
          </span>
        </label>
        <button
          type="button"
          onClick={() => setTermsOpen((o) => !o)}
          aria-expanded={termsOpen}
          className="mt-3 font-dm-sans text-[13px] font-medium text-[rgba(212,168,67,0.75)] hover:text-[#C9A84C] hover:underline underline-offset-2 transition-colors duration-200"
        >
          {termsOpen ? 'Hide the terms' : 'Read the terms'}
        </button>
        {termsOpen && (
          <ol className="flex flex-col gap-4 mt-4 pt-4 border-t border-white/[0.06]">
            {clauses.map((clause, i) => (
              <li key={clause.title}>
                <h3 className="font-dm-sans text-[13px] font-semibold text-[#C9A84C] mb-1">{i + 1}. {clause.title}</h3>
                <p className="font-dm-sans text-[13px] font-light text-white/60 leading-relaxed">{clause.body}</p>
              </li>
            ))}
          </ol>
        )}
      </div>

      {errorMsg && (
        <p className="font-dm-sans text-sm font-light text-white/60 leading-relaxed mb-3 text-center">{errorMsg}</p>
      )}
      <button
        type="button"
        onClick={handlePay}
        disabled={!canPay}
        className={`${btnPrimary} ${canPay ? btnEnabled : btnDisabled}`}
      >
        {phase === 'booking'
          ? 'Confirming your booking...'
          : phase === 'verifying'
          ? 'Verifying payment...'
          : phase === 'working'
          ? 'Opening secure checkout...'
          : `Pay ₹${Number(link.amount).toLocaleString('en-IN')}`}
      </button>
      {!agreed && !busy && (
        <p className="font-dm-sans text-center text-[13px] font-light text-white/45 mt-3 leading-relaxed">
          Tick the box above to continue
        </p>
      )}
      <p className="font-dm-sans text-center text-sm text-white/55 mt-3 mb-2 leading-relaxed">
        Payments are processed securely by Razorpay
      </p>
    </div>
  );
}
